import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, EMPTY, tap } from 'rxjs';

import { LicensingStateModel, OrganizationLicense } from '@shared/models';
import { LicensingApiService } from '@shared/services/licensing-api/licensing-api.service';

export const defaultLicensingState: LicensingStateModel = {
  licenses: [],
  isLoading: false,
  error: undefined,
  success: undefined,
};

const beforeApiCall: Partial<LicensingStateModel> = {
  isLoading: true,
  error: undefined,
  success: undefined,
};

const errorStatus = (error: Error): Partial<LicensingStateModel> => ({
  error: error.message,
  isLoading: false,
  success: undefined,
  licenses: [],
});

const successStatus = ({
  success,
  licenses,
}: {
  success?: string;
  licenses: OrganizationLicense[];
}): Partial<LicensingStateModel> => ({
  success,
  licenses,
  isLoading: false,
  error: undefined,
});

@Injectable({ providedIn: 'root' })
export class LicensingStore {
  private readonly licensingApi = inject(LicensingApiService);

  private readonly state = signal<LicensingStateModel>(defaultLicensingState);

  readonly licenses = computed(() => this.state().licenses);
  readonly selected = computed(() => this.state().selected);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly error = computed(() => this.state().error);
  readonly success = computed(() => this.state().success);

  fetchLicense(): void {
    this.patch(beforeApiCall);
    this.licensingApi
      .fetchLicense()
      .pipe(
        tap((licenses) => this.patch(successStatus({ licenses }))),
        catchError((error: Error) => {
          this.patch(errorStatus(error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  saveOrganizationLicense(newLicense: OrganizationLicense): void {
    this.patch(beforeApiCall);
    this.licensingApi
      .saveOrganizationLicense(newLicense)
      .pipe(
        tap((license) => this.patch(successStatus({ success: 'LICENSING.NEW_ORG.SUCCESS', licenses: [license] }))),
        catchError((error: Error) => {
          this.patch(errorStatus(error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  patchSelectedLicense(selected?: OrganizationLicense): void {
    this.patch({ selected });
  }

  reset(): void {
    this.state.set(defaultLicensingState);
  }

  private patch(partial: Partial<LicensingStateModel>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }
}
